import { i18nLocale } from '../translate/translate';
import { UploadResponse } from '../upload/upload';
import { AiWaltCommand } from '../voice-assistant/voice-assistant-models';
import { ChannelType } from './channel';
import { ChannelNotify } from './channel-notifications';


const ZIP_MIME_TYPES: string[] = [
    'application/zip',
    'application/x-zip-compressed',
    'multipart/x-zip',
    'application/x-gzip',
    'multipart/x-gzip',
    'application/x-compressed',
    'application/x-zip-compressed',
    'application/x-zip-compressed',
];

export const ALLOWED_FILE_MIME_TYPES: string[] = [
    'image/jpeg',
    'image/png',
    'image/gif',
    'application/pdf',
    'application/msword',
    'text/plain',
    'video/quicktime',
    'video/mp4',
    'video/mp2t',
    'video/mpeg',
    'audio/aac',
    'audio/mp4',
    'audio/ac3',
    'application/x-cfb', // .doc, .xls
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
    'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx
    'application/vnd.ms-powerpoint', // .ppt
    'application/vnd.ms-excel', // .xls
    'application/vnd.ms-powerpoint', // .pps
    'application/vnd.openxmlformats-officedocument.presentationml.slideshow', // .ppsx
    'application/vnd.oasis.opendocument.text', // .odt
    'application/vnd.oasis.opendocument.spreadsheet', // .ods
    'application/vnd.oasis.opendocument.presentation', // .odp
].concat(ZIP_MIME_TYPES);

export enum MessageType {
    Audio = 'audio',
    Form = 'form',
    Page = 'page',
    Text = 'text',
    Video = 'video',
}

export enum FilterableContentType {
    Image = 'image',
    Video = 'video',
    File = 'file',
    Audio = 'audio',
    Link = 'link',
}

export enum ChannelSource {
    Mobile = 'mobile',
    Walt = 'walt',
    Web = 'web',
}

export enum SystemMessageType {
    AddedMember = 'added-member',
    AdminApi = 'admin-api',
    AiVoiceAssistant = 'ai-voice-assistant',
    AiVoiceAssistantAction = 'ai-voice-assistant-action',
    AnsweredCall = 'answered-call',
    AnsweredPage = 'answered-page',
    AutoReply = 'auto-reply',
    CalledChannel = 'called-channel',
    DeclinedCall = 'declined-call',
    DeclinedPage = 'declined-page',
    LeftChannel = 'left-channel',
    MissedCall = 'missed-call',
    MissedPage = 'missed-page',
    Paged = 'paged',
    RemovedMember = 'removed-member',
    RuleTriggered = 'rule-triggered',
    SupportTicketStatusChange = 'support-ticket-status-change',
}

// These types will be styled like a normal user message
export const USER_STYLED_SYSTEM_MESSAGE_TYPES: SystemMessageType[] = [
    SystemMessageType.AdminApi,
    SystemMessageType.RuleTriggered,
    SystemMessageType.AiVoiceAssistant,
];

export enum MessageAttachmentType {
    Image = 'image',
    Video = 'video',
    File = 'file',
    Audio = 'audio',
    Link = 'link', // used in iOS share extension. but what is it?
    /** Announced over the mesh but not uploaded yet; carries no `file` until completed. */
    Pending = 'pending',
}

export interface ChannelMessage {
    id?: {
        channelId?: string;
        date: Date;
        id?: string;
    };
    data?: ChannelMessageData;
    fromTeams?: boolean;
    location?: number[];
    message?: string;
    refId?: string;
    sender?: { // populated if not a system message
        id?: string;
    };
    sequence?: number;
    source?: ChannelSource;
    timedOut?: boolean;
    translations?: { [locale in i18nLocale]?: string };
    translationsSegments?: { [locale in i18nLocale]?: string[] };
    type?: MessageType;
    uploaded?: Date;
    offline?: boolean; // indicates the message was sent while offline and is being uploaded to the server
    edited?: Date;
    /** indicates a synthetic incoming PTT, sent by the Two Walt Demo or the PTT health check */
    spoofed?: boolean;
    streamingState?: 'streaming' | 'complete';
}

export interface ExternalApiChannelMessage {
    id: string;
    channelId: string;
    date: Date;
    message: string;
    sender: {
        id: string;
        firstName: string;
        lastName: string;
        avatarFile: string;
    };
    type: MessageType;
    data: ChannelMessageData;
    edited: Date;
}

export interface ExternalApiChannelMessageResponse {
    messages: ChannelMessageResponse[];
    next: string | undefined;
}

export interface ExternalApiChannelMessageRequest {
    fromDate?: Date;
    toDate?: Date;
    contentTypes?: FilterableContentType[];
    next?: string;
    toId?: string;
    pageSize?: number;
}

export interface ChannelMessageData {
    attachments?: MessageAttachment[];
    buttons?: { acceptButtonText?: string, cancelButtonText?: string };
    duration?: number; // audio attachments only
    event?: any;
    form?: { id?: string, version?: string, name?: string, title?: string, facilityId?: string };
    forms?: MessageFormLink[]; // forms that are being sent in the message
    linkPreviews?: LinkPreview[] | URLLinkPreview[];
    links?: string[];
    locale?: string;
    dialect?: string;
    /** For auto-replies, shows who the auto-reply came from. */
    onBehalfOf?: {
        userId: string;
        fullName: string;
        locale?: string;
        dialect?: string;
    };
    pager?: { id?: string, firstName?: string, lastName?: string, message?: string };
    radio?: string; // "<serial> - <radio>"
    ruleId?: string;
    systemMessageType?: SystemMessageType; // if it is a system message
    systemMessageData?: SystemMessageData; // data related to system message information
    supportTicketAssignee?: string; // for support tickets, shows who the ticket is assigned to - likely needs to be consolidated into a onBehalfOf concept... someday.
    aiQuestion?: boolean;
}

export interface SystemMessageData {
    sender?: string; // String placed in the sender's name place on messages. Defaults to 'weavix integration' if not provided
    readAloud?: boolean; // Will force the system message to read aloud despite the user's settings
    accountId?: string; // The account id of the system message sender
    aiWaltCommand?: AiWaltCommand;
    aiResponseToChannelMessageId?: string; // The channel message id that the AI is responding to
    aiSessionTimedOut?: boolean; // If the AI session timed out
}

export interface MessageAttachment {
    type: MessageAttachmentType;
    id?: string;
    duration?: number;
    /** Absent on a pending placeholder. */
    file?: UploadResponse;
    thumbnail?: UploadResponse; // if type === image || type === video
    thumbnailUri?: string;
    trimEnd?: number;
    /** Present only while `type` is {@link MessageAttachmentType.Pending}. */
    pending?: PendingMedia;
}

/**
 * Terms on which a pending attachment may be completed. Named to stay clear of web radio's
 * `PendingAttachment`, which means a locally-selected file that has not been sent yet.
 */
export interface PendingMedia {
    /** The type the completed upload has to be. */
    expect: MessageAttachmentType.Image | MessageAttachmentType.Video;
    /** Past this point the placeholder cannot be completed and reads as failed. */
    expires: Date;
}

export interface CarouselAttachment {
    id: string | undefined;
    name?: string;
    date: string | Date | undefined;
    sender?: { // populated if not a system message
        id?: string;
        firstName?: string;
        lastName?: string;
        avatarFile?: string;
    };
    messageId: string | undefined;
    attachment: MessageAttachment;
}

export function channelMessageToCarouselAttachment(message: ChannelMessageResponse): CarouselAttachment[] {
    return message.data?.attachments
        ? message.data.attachments
            .filter(x => ['image', 'video'].includes(x.type))
            .map(attachment => ({
                id: attachment.file?.id,
                name: attachment.file?.name,
                date: typeof message.date === 'string' ? message.date : message.date?.toISOString(),
                sender: message.sender,
                messageId: message.id,
                attachment,
            }))
        : [];
}

export interface MessageFormLink {
    accountId: string;
    formId: string;
}

export function createMessageFormLinkString(accountId: string, formId: string): string {
    return `weavix://form/${accountId}/${formId}`;
}


export const FORM_LINK_REGEX = /weavix:\/\/form\/([^/]+)\/([^/]+)/;

export function readMessageFormLinkString(link: string): MessageFormLink | undefined {
    const match = FORM_LINK_REGEX.exec(link);
    if (match) {
        return {
            accountId: match[1],
            formId: match[2],
        };
    }
    return undefined;
}


export interface LinkPreview {
    url: string;
    title?: string;
    siteName?: string;
    description?: string;
    imageUri?: string;
    images?: string[];
    videos?: string[];
    favicons?: string[];
}

export interface URLLinkPreview extends LinkPreview {
    isLoading?: boolean;
}

export interface Communication {
    id?: string;
    date: Date | string;
    facilityId: string;
    offsite: boolean;
    onsite?: boolean;
    personId?: string;
    type?: MessageType;
    location?: number[];
    source?: ChannelSource;
    duration?: number;
    count?: number;
}

export interface CreateMessageRequest {
    data?: ChannelMessageData;
    date?: Date;
    edited?: Date;
    fromTeams?: boolean;
    message?: string;
    recorded?: Date;
    refId?: string;
    sequence?: number;
    source?: ChannelSource;
    timedOut?: boolean;

    type?: MessageType;
    /** indicates a synthetic incoming PTT, sent by the Two Walt Demo or the PTT health check */
    spoofed?: boolean;
    translations?: { [locale in i18nLocale]?: string };
    translationsSegments?: { [locale in i18nLocale]?: string[] };
    streamingState?: 'streaming' | 'complete';
}

export interface ChannelMessageResponse extends ChannelNotify {
    id?: string;
    channelId?: string;
    channelName?: string;
    channelType?: ChannelType;
    data?: ChannelMessageData;
    date?: Date | string; // it's a Date on the backend, but gets transmitted over the wire as a string
    fromTeams?: boolean;
    message?: string;
    refId?: string;
    sender?: { // populated if not a system message
        id?: string;
        firstName?: string;
        lastName?: string;
        avatarFile?: string;
    };
    type?: MessageType;
    edited?: Date;
}

export interface CallingSystemMessageRequest {
    type: SystemMessageType;
    /**
     * True if the message is for the user on the channel; false if it's for the current user.
     * Used to send "{other user} missed the call" system message.
     */
    forOtherUser?: boolean;
}

export interface ChannelPage {
    id?: string;
    message?: string;
}

export interface PageResponse {
    /** the id of this response notification */
    id?: string;
    /** the id of the notification this is responding to, only set for ACKs */
    respondingTo?: string;
    answered?: boolean;
    declined?: boolean;
    missed?: boolean;
}

export interface ChannelMessageQuery {
    toId?: string;
    toDate?: string;
    limit?: number;
    contentType?: MessageAttachmentType[];
}

export interface ApiChannelMessageRequest {
    message: string;
    senderName?: string;
    readAloud?: boolean;
}

export interface NewRelicChannelMessageRequest {
    id: string;
    title: string;
    issueUrl: string;
    priority: string;
    impactedEntities: string[];
}

export interface ApiAdminMessageRequest {
    userId: string;
    message: string;
    channelName: string;
    senderName: string;
    messageLanguage?: string;
    readAloud?: boolean;
    mediaUrl?: string;
    channelImageUrl?: string;
    responseUrl?: string;
}

/**
 * A PTT used during Two Walt Demo mode to seed an account with pre-existing conversations.
 */
export interface CannedPttRequest {
    destinationAccountId: string;
    destinationChannelId: string;
    message: string;
    messageLanguage: 'en' | 'es';
    senderUserId: string;
}

export interface PreRecordedPttRequest extends CannedPttRequest {
    recordingId: string;
}

/**
 * Whether to show the offline-upload indicator for a message.
 *
 * `offline` is only present on messages the server has stored since the field was added, so fall
 * back to `uploaded` for anything older -- that date is what the indicator used to be driven by.
 */
export function isOfflineUpload(message?: { offline?: boolean, uploaded?: Date }): boolean {
    return typeof message?.offline === 'boolean' ? message.offline : !!message?.uploaded;
}
